import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const KEY = 'ca_insurer_tour';
const EVT = 'ca-insurer-tour';

export const TOUR_STEPS = [
  { route: () => '/insurance', target: '[data-tour="stats"]', title: 'Resumen del panel',
    body: 'Cuatro indicadores en vivo de tu compañía: total de servicios, activos en este momento, completados y el tiempo promedio que tarda C&A en asignar una grúa (minutos).' },
  { route: () => '/insurance', target: '[data-tour="list"]', title: 'Listado y estados',
    body: 'Cada fila es un servicio de tus asegurados: número C&A, cliente, vehículo, placa y póliza. La etiqueta de la derecha muestra el estado en tiempo real (Nuevo → Asignado → En ruta → Cargado → Entregado → Completado). El buscador filtra por número, cliente, placa o póliza; al pulsar una fila se abre el detalle.' },
  { route: () => '/insurance', target: '[data-testid="btn-new-request"]', title: 'Nueva solicitud',
    body: 'Desde aquí la aseguradora pide una grúa para un asegurado: datos del cliente y del vehículo, punto de recogida y destino en el mapa, tipo de servicio y número de póliza. Al enviarla, el despacho de C&A la recibe al instante y el asegurado obtiene su enlace de rastreo. En este recorrido no se envía ninguna solicitud.' },
  { route: (sid) => `/service/${sid}`, target: '[data-tour="svc-info"]', title: 'Detalle del servicio',
    body: 'Ficha completa del caso: cliente, teléfono, tipo de servicio, vehículo, placa, póliza, origen y destino. En el mapa se ve el punto de recogida (C), el destino (B), la ruta y la posición de la unidad asignada.' },
  { route: (sid) => `/service/${sid}`, target: '[data-tour="timeline"]', title: 'Historial del servicio',
    body: 'Cada cambio de estado queda registrado con fecha, hora y responsable: creación, asignación, aceptación del conductor, llegada, carga, transporte, entrega y cierre. Más abajo, la pestaña «Vista del cliente» muestra exactamente los mensajes que recibe el asegurado.' },
  { route: (sid) => `/service/${sid}`, target: '[data-tour="evidence"]', title: 'Fotos y firma',
    body: 'El conductor fotografía el vehículo antes de cargarlo y al entregarlo (mínimo 4 fotos antes y 2 después) y el receptor firma en pantalla. Todo queda ligado al servicio: se consulta aquí y se incluye en el reporte PDF.' },
  { route: (sid) => `/service/${sid}`, target: '[data-tour="invoice"]', title: 'Precio y factura',
    body: 'Precio del servicio según la tarifa pactada con la compañía. Al completarse, C&A emite la factura con NCF; su número aparece aquí y la factura completa se consulta y descarga en PDF desde «Facturas» en el panel.' },
  { route: (sid) => `/service/${sid}`, target: '[data-testid="btn-executive-pdf"]', title: 'Reporte ejecutivo',
    body: 'PDF de dos páginas listo para el expediente del siniestro: datos del servicio, línea de tiempo, fotos y firma de recibido. Se abre en una pestaña nueva.' },
  { route: () => '/insurance', target: '[data-tour="toolbar"]', title: 'Reportes y facturación',
    body: '«Reporte PDF» genera el consolidado del mes seleccionado; «Facturas» lista todas las facturas emitidas a tu compañía con su estado y descarga en PDF; «Galería» reúne las fotos de todos los servicios y «Scorecard» muestra la satisfacción de tus asegurados. Fin del recorrido.' },
];

const read = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) || null; } catch { return null; } };
const write = (v) => { if (v) sessionStorage.setItem(KEY, JSON.stringify(v)); else sessionStorage.removeItem(KEY); window.dispatchEvent(new Event(EVT)); };

export function startInsurerTour(serviceId) { write({ step: 0, serviceId }); }

export default function InsurerTourOverlay() {
  const [state, setState] = useState(read);
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const cardRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => { const h = () => setState(read()); window.addEventListener(EVT, h); return () => window.removeEventListener(EVT, h); }, []);

  const step = state ? TOUR_STEPS[state.step] : null;
  const wanted = step ? step.route(state.serviceId) : null;

  useEffect(() => { if (wanted && pathname !== wanted) nav(wanted); }, [wanted, pathname, nav]);

  useEffect(() => {
    if (!step || pathname !== wanted) return;
    let el = null, tries = 0, raf = 0, cancelled = false;
    setRect(null); setMissing(false);
    const measure = () => { if (el && !cancelled) { const r = el.getBoundingClientRect(); setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); } };
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    const find = () => {
      if (cancelled) return;
      el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(measure, 450); setTimeout(measure, 900);
        window.addEventListener('scroll', onMove, true); window.addEventListener('resize', onMove);
      } else if (++tries < 60) setTimeout(find, 150);
      else setMissing(true);
    };
    find();
    return () => { cancelled = true; cancelAnimationFrame(raf); window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove); };
  }, [step, pathname, wanted]);

  const go = useCallback((delta) => {
    const next = state.step + delta;
    if (next < 0) return;
    if (next >= TOUR_STEPS.length) { write(null); return; }
    write({ ...state, step: next });
  }, [state]);
  const exit = useCallback(() => write(null), []);

  useEffect(() => {
    if (!state) return;
    const h = (e) => { if (e.key === 'Escape') exit(); else if (e.key === 'ArrowRight') go(1); else if (e.key === 'ArrowLeft') go(-1); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [state, go, exit]);

  if (!state || !step) return null;
  const pad = 8;
  const last = state.step === TOUR_STEPS.length - 1;

  let cardStyle;
  if (isMobile) cardStyle = { left: 12, right: 12, bottom: 12 };
  else if (rect) {
    const W = 380, H = (cardRef.current?.offsetHeight || 230), vw = window.innerWidth, vh = window.innerHeight;
    const right = rect.left + rect.width;
    let top, left;
    if (rect.top + rect.height + pad + H < vh) { top = rect.top + rect.height + pad + 6; left = rect.left; }
    else if (rect.top - pad - H > 0) { top = rect.top - pad - H + 4; left = rect.left; }
    else if (rect.left - pad - W > 0) { top = Math.max(12, rect.top); left = rect.left - pad - W - 6; }
    else if (vw - right - pad - W > 0) { top = Math.max(12, rect.top); left = right + pad + 6; }
    else { top = 12; left = rect.left; }
    cardStyle = { top: Math.min(top, vh - H - 12), left: Math.min(Math.max(12, left), vw - W - 12), width: W };
  } else cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 380 };

  return (
    <div className="fixed inset-0 z-[1000]" data-testid="insurer-tour" aria-live="polite">
      <div className="absolute inset-0" style={{ background: rect ? 'transparent' : 'rgba(0,0,0,.72)' }} onClick={(e) => e.stopPropagation()} />
      {rect && (
        <div className="absolute rounded-xl pointer-events-none transition-all duration-300"
          data-testid="tour-spotlight"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
                   boxShadow: '0 0 0 9999px rgba(0,0,0,.72), 0 0 0 2px #E63946, 0 0 28px rgba(230,57,70,.55)' }} />
      )}
      <div ref={cardRef} role="dialog" aria-label={step.title} data-testid="tour-card"
        className="fixed bg-[#0D0D0D] border border-[#E63946]/50 rounded-xl shadow-2xl p-4 sm:p-5 text-neutral-200"
        style={cardStyle}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E63946] font-bold" data-testid="tour-step-counter">Recorrido · {state.step + 1} / {TOUR_STEPS.length}</div>
            <h3 className="font-display text-lg font-black leading-tight mt-0.5" data-testid="tour-title">{step.title}</h3>
          </div>
          <button onClick={exit} aria-label="Salir del recorrido" data-testid="tour-exit" className="text-neutral-500 hover:text-white p-1 -m-1"><X size={18} /></button>
        </div>
        <p className="text-sm text-neutral-400 leading-relaxed" data-testid="tour-body">{step.body}</p>
        {missing && <p className="text-xs text-amber-400 mt-2">Esta sección no está visible en el servicio de ejemplo; continúa con «Siguiente».</p>}
        <div className="flex items-center justify-between gap-2 mt-4">
          <button onClick={exit} data-testid="tour-exit-btn" className="text-xs text-neutral-500 hover:text-neutral-200 px-2 py-2">Salir</button>
          <div className="flex gap-2">
            <button onClick={() => go(-1)} disabled={state.step === 0} data-testid="tour-prev"
              className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md border border-neutral-800 hover:border-neutral-600 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft size={16} /> Anterior
            </button>
            <button onClick={() => go(1)} data-testid="tour-next"
              className="inline-flex items-center gap-1 text-sm font-semibold px-4 py-2 rounded-md bg-[#E63946] hover:bg-[#D90429] text-white">
              {last ? 'Finalizar' : 'Siguiente'} {!last && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
